import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { champ, PRIORITES } from './ui';

/* Les actions du plan travaillées pendant une séance.

   Une liste de cases à plat ne se lisait plus au-delà d'une dizaine
   d'actions : c'est désormais une liste déroulante, filtrable par urgence
   et par texte, toujours à sélection multiple. Les actions choisies restent
   visibles sous le bouton, chacune retirable d'un clic. */
export default function SelecteurActions({ actions, valeur, onChange }) {
  const [ouvert, setOuvert] = useState(false);
  const [priorite, setPriorite] = useState('');
  const [texte, setTexte] = useState('');
  const zone = useRef(null);
  const ordre = Object.keys(PRIORITES);

  // Un clic hors de la liste la referme, sans fermer la fenêtre autour.
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e) => { if (zone.current && !zone.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  const triees = useMemo(() => actions.slice().sort((a, b) =>
    (ordre.indexOf(a.priorite) - ordre.indexOf(b.priorite))
    || String(a.echeance || '9999').localeCompare(String(b.echeance || '9999'))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actions]);
  const visibles = triees
    .filter((a) => !priorite || a.priorite === priorite)
    .filter((a) => !texte.trim() || `${a.description} ${a.domaine_nom} ${a.sous_domaine_nom}`
      .toLowerCase().includes(texte.trim().toLowerCase()));
  const choisies = triees.filter((a) => valeur.includes(a.id));
  const basculer = (id) => onChange(valeur.includes(id) ? valeur.filter((x) => x !== id) : [...valeur, id]);

  return (
    <div ref={zone} className="relative">
      <span className="block text-xs font-semibold text-[var(--text-muted)] mb-1">Actions du plan travaillées pendant la séance</span>
      <button type="button" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert} aria-haspopup="listbox"
        className={`${champ} flex items-center justify-between gap-2 text-left`}>
        <span className={choisies.length ? 'text-[var(--text-bold)] font-semibold' : 'text-[var(--text-muted)]'}>
          {choisies.length ? `${choisies.length} action${choisies.length > 1 ? 's' : ''} sélectionnée${choisies.length > 1 ? 's' : ''}` : 'Choisir des actions…'}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
      </button>

      {ouvert && (
        <div className="mt-1 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] shadow-carte p-3"
             style={{ boxShadow: '0 18px 40px -12px rgba(10,17,40,.35)' }}>
          <div className="flex flex-wrap gap-1.5 mb-2" role="group" aria-label="Filtrer par urgence">
            {[['', 'Toutes', actions.length], ...ordre.map((p) => [p, PRIORITES[p].label, actions.filter((a) => a.priorite === p).length])]
              .filter(([cle, , n]) => !cle || n > 0)
              .map(([cle, libelle, n]) => (
                <button key={cle || 'toutes'} type="button" aria-pressed={priorite === cle} onClick={() => setPriorite(cle)}
                  className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${priorite === cle
                    ? 'bg-[var(--accent)] border-[var(--accent)] text-[#0A1128]'
                    : 'border-[var(--border)] text-[var(--text-main)] hover:bg-[var(--bg-main)]'}`}>
                  {libelle} <span className="opacity-70 tabular-nums">{n}</span>
                </button>
              ))}
          </div>
          <label className="relative block mb-2">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" aria-hidden="true" />
            <input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Rechercher une action…"
              aria-label="Rechercher une action" className={`${champ} pl-8 py-2`} />
          </label>
          <ul role="listbox" aria-multiselectable="true" className="max-h-60 overflow-y-auto space-y-0.5 pr-1">
            {visibles.length === 0 && <li className="text-xs text-[var(--text-muted)] py-3 text-center">Aucune action pour ce filtre.</li>}
            {visibles.map((a) => (
              <li key={a.id} role="option" aria-selected={valeur.includes(a.id)}>
                <label className="flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-[var(--bg-main)]">
                  <input type="checkbox" className="mt-1 shrink-0" checked={valeur.includes(a.id)} onChange={() => basculer(a.id)} />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {PRIORITES[a.priorite] && (
                        <span className={`inline-flex px-1.5 py-px rounded border text-[10px] font-semibold ${PRIORITES[a.priorite].className}`}>
                          {PRIORITES[a.priorite].label}
                        </span>
                      )}
                      {(a.domaine_nom || a.sous_domaine_nom) && (
                        <span className="text-[11px] text-[var(--text-muted)]">{[a.domaine_nom, a.sous_domaine_nom].filter(Boolean).join(' · ')}</span>
                      )}
                    </span>
                    <span className="block text-sm text-[var(--text-main)] line-clamp-2">{a.description}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[var(--border)]">
            <button type="button" onClick={() => onChange([])} disabled={!valeur.length}
              className="text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--text-bold)] disabled:opacity-40">Tout décocher</button>
            <button type="button" onClick={() => setOuvert(false)}
              className="text-xs font-semibold text-[var(--accent-texte)]">Valider</button>
          </div>
        </div>
      )}

      {choisies.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mt-2">
          {choisies.map((a) => (
            <li key={a.id} className="inline-flex items-center gap-1 max-w-full rounded-lg bg-[var(--bg-main)] border border-[var(--border)] pl-2 pr-1 py-0.5 text-xs text-[var(--text-main)]">
              <span className="truncate max-w-[16rem]" title={a.description}>{a.description}</span>
              <button type="button" onClick={() => basculer(a.id)} aria-label={`Retirer : ${a.description}`}
                className="p-0.5 rounded hover:bg-[var(--bg-card)]"><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
